<script setup lang="ts">
// Tournament DETAIL (round-10, 2026-08-31) — split out of the old all-in-one Tournaments.vue.
// Reached from the list via #tournamentDetail?tournamentId=N; back button returns to the list.
// Three states, driven by the tournament's own status (NOT the active-tournament store — this
// screen can show any historical tournament):
//   registration  -> participants + scan/manual add + "Generate"
//   in_progress   -> phase explorer (below) with Start/Live/View result on each pairing
//   finished      -> same explorer, plus winner banner (+ prize if set)
// Phase explorer by format:
//   - group phase (round_robin / rr_knockout): STANDINGS table + circle-method rounds as
//     horizontally scrollable columns ("Round 1..N-1")
//   - knockout (single_elim, or rr_knockout once the phase completes): classic bracket —
//     columns per round, cards vertically justified (justify-around) so each match sits midway
//     between its two feeders; labels "Round of 16 → Quarterfinals → Semifinals → Final".
// Match hand-off is unchanged: a ready pairing's Start prefills New match via
// prefillFromTournamentSlot; winner advancement/standings are pure server-side effects and this
// view just re-fetches (SSE-driven refresh: any liveMatch transition triggers loadDetail).
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { mdiArrowLeft, mdiTrophy } from '@mdi/js'
import { generateBracket, getTournament, removeTournamentParticipant, addTournamentParticipant, ApiRequestError } from '../backend'
import { nav, navigate } from '../store/nav'
import { tableStore } from '../store/table'
import { refreshActiveTournament } from '../store/tournament'
import { prefillFromTournamentSlot } from '../store/draft'
import { startRollCall, cancelRollCall } from '../services/rollCall'
import type { Player, TournamentBracketSlot, TournamentDetail } from '../types'
import Avatar from '../components/Avatar.vue'
import PlayerSearchList from '../components/PlayerSearchList.vue'
import RollCallPanel from '../components/RollCallPanel.vue'

const detail = ref<TournamentDetail | null>(null)
const loadError = ref(false)
const addPickerOpen = ref(false)
const actionError = ref<string | null>(null)
const busyPlayerId = ref<number | null>(null)
const generating = ref(false)

const tournamentId = computed(() => nav.params.tournamentId ?? null)

async function loadDetail() {
  const id = tournamentId.value
  if (id == null) {
    navigate('tournaments')
    return
  }
  try {
    detail.value = await getTournament(id)
    loadError.value = false
  } catch {
    loadError.value = true
  }
}

onMounted(loadDetail)
watch(tournamentId, loadDetail)
// A tournament match finishing shows up as the table's liveMatch going away — cheap, already
//-streaming signal to re-fetch standings/advancement without any tournament-specific SSE.
watch(() => tableStore.state?.liveMatch?.id ?? null, () => void loadDetail())

const winner = computed(() => {
  const d = detail.value
  if (!d || d.winnerPlayerId == null) return null
  return d.participants.find((p) => p.player.id === d.winnerPlayerId)?.player ?? null
})

const knockoutDone = computed(() => (detail.value?.rounds.length ?? 0) > 0)

async function onPickManual(player: Player) {
  const d = detail.value
  if (!d) return
  actionError.value = null
  try {
    await addTournamentParticipant(d.id, player.id)
    await loadDetail()
  } catch (e) {
    actionError.value =
      e instanceof ApiRequestError && e.code === 'already_registered' ? `${player.displayName} is already registered` : e instanceof ApiRequestError ? e.message : 'Could not add player'
  }
}

async function onRemove(playerId: number) {
  const d = detail.value
  if (!d) return
  busyPlayerId.value = playerId
  actionError.value = null
  try {
    await removeTournamentParticipant(d.id, playerId)
    await loadDetail()
  } catch (e) {
    actionError.value = e instanceof ApiRequestError ? e.message : 'Could not remove player'
  } finally {
    busyPlayerId.value = null
  }
}

// Round-29 (2026-08-31): roll call — TTS-announces + asks each registered participant in turn
// ("Luuk, ¿estás listo?"), see services/rollCall.ts + components/RollCallPanel.vue. Candidate
// scope for THIS tournament's speaker-match sanity check is its own participants only
// (candidateScope.ts's 'tournament' tier) — rollCall.ts derives that from rollCallState's own
// `rows`, not threaded through here. Cancelled on unmount (below) same as confirmFlow.ts's
// confirmations cancel on navigating away.
function onRollCall() {
  const d = detail.value
  if (!d || d.participants.length === 0) return
  startRollCall(
    d.id,
    d.participants.map((p) => p.player),
  )
}
onUnmounted(() => cancelRollCall())

async function onGenerate() {
  const d = detail.value
  if (!d || generating.value) return
  generating.value = true
  actionError.value = null
  try {
    await generateBracket(d.id)
    await refreshActiveTournament()
    await loadDetail()
  } catch (e) {
    actionError.value = e instanceof ApiRequestError ? e.message : 'Could not generate'
  } finally {
    generating.value = false
  }
}

function startSlot(slot: TournamentBracketSlot) {
  if (!slot.playerA || !slot.playerB) return
  prefillFromTournamentSlot(slot.playerA, slot.playerB, slot.id)
  navigate('newMatch')
}

function openSlotMatch(slot: TournamentBracketSlot) {
  if (slot.matchId == null) return
  if (slot.winnerPlayerId == null) navigate('live')
  else navigate('pastMatchDetail', { matchId: slot.matchId })
}

// --- Knockout bracket geometry (round-11: connector lines) -------------------------------
// Cards are ABSOLUTELY positioned on a fixed grid instead of flex justify-around, so every
// card's vertical center is computable and an SVG underlay can draw the elbow connectors:
// round r slot i is centered at PITCH * 2^r * (i + 0.5); its feeders are round r-1 slots
// 2i/2i+1 (same arithmetic the backend uses). A connector is drawn PER FEEDER (stub → mid
// vertical → target stub) and turns emerald once that feeder has resolved — so the winner's
// path lights up round by round.
const COL_W = 240 // w-60
const COL_GAP = 48 // connector corridor
const PITCH = 118 // round-0 vertical rhythm (card ~104px + breathing room)
const LABEL_H = 28

const bracketGeom = computed(() => {
  const rounds = detail.value?.rounds ?? []
  if (rounds.length === 0) return null
  const n0 = rounds[0].length
  const width = rounds.length * (COL_W + COL_GAP) - COL_GAP
  const height = LABEL_H + n0 * PITCH
  const centerY = (ri: number, i: number) => LABEL_H + PITCH * 2 ** ri * (i + 0.5)
  const colX = (ri: number) => ri * (COL_W + COL_GAP)
  const connectors: { d: string; won: boolean }[] = []
  for (let ri = 1; ri < rounds.length; ri++) {
    for (let i = 0; i < rounds[ri].length; i++) {
      const endX = colX(ri)
      const startX = colX(ri - 1) + COL_W
      const midX = startX + COL_GAP / 2
      const yTarget = centerY(ri, i)
      for (const fi of [2 * i, 2 * i + 1]) {
        const feeder = rounds[ri - 1][fi]
        if (!feeder) continue
        const yf = centerY(ri - 1, fi)
        connectors.push({
          d: `M ${startX} ${yf} H ${midX} V ${yTarget} H ${endX}`,
          won: feeder.winnerPlayerId != null,
        })
      }
    }
  }
  return { width, height, centerY, colX, connectors }
})

// "Rounds of N, then X-finals": Final / Semifinals / Quarterfinals, everything earlier is
// "Round of <players left>".
function knockoutRoundLabel(roundIndex: number, totalRounds: number): string {
  const fromEnd = totalRounds - roundIndex
  if (fromEnd === 1) return 'Final'
  if (fromEnd === 2) return 'Semifinals'
  if (fromEnd === 3) return 'Quarterfinals'
  return `Round of ${2 ** fromEnd}`
}
</script>

<template>
  <div class="flex-1 min-h-0 flex flex-col p-4 gap-3">
    <!-- Header: back + name + status/prize -->
    <div class="flex items-center gap-3">
      <button
        type="button"
        class="tap-target w-10 h-10 min-h-0 min-w-0 shrink-0 rounded-full active:bg-neutral-800 flex items-center justify-center text-neutral-400"
        aria-label="Back to tournaments"
        @click="navigate('tournaments')"
      >
        <svg viewBox="0 0 24 24" class="w-5 h-5" fill="currentColor"><path :d="mdiArrowLeft" /></svg>
      </button>
      <div class="min-w-0 flex-1">
        <h1 class="text-xl font-bold truncate">{{ detail?.name ?? '…' }}</h1>
        <div v-if="detail" class="text-[11px] font-mono text-neutral-500">
          <span v-if="detail.status === 'registration'">Registration open</span>
          <span v-else-if="detail.status === 'in_progress'">In progress</span>
          <span v-else>Finished</span>
          <template v-if="detail.prize"> · 🏆 {{ detail.prize }}</template>
        </div>
      </div>
    </div>

    <div v-if="loadError" class="flex-1 flex items-center justify-center text-neutral-500">Unavailable</div>
    <template v-else-if="detail">
      <!-- Winner banner -->
      <div v-if="winner" class="rounded-2xl border border-amber-600/40 bg-amber-950/30 px-4 py-3 flex items-center gap-3">
        <svg viewBox="0 0 24 24" class="w-7 h-7 text-amber-400 shrink-0" fill="currentColor"><path :d="mdiTrophy" /></svg>
        <Avatar :player="winner" :size="44" />
        <div class="min-w-0 flex-1">
          <div class="font-bold truncate">{{ winner.displayName }} wins {{ detail.name }}</div>
          <div v-if="detail.prize" class="text-sm text-amber-300/80 truncate">Prize: {{ detail.prize }}</div>
        </div>
      </div>

      <p v-if="actionError" class="text-sm text-red-400">{{ actionError }}</p>

      <!-- Registration -->
      <template v-if="detail.status === 'registration'">
        <div class="rounded-xl bg-neutral-900 border border-neutral-800 px-4 py-3">
          <div class="text-sm text-neutral-500">Scan a badge to register, or add someone manually below.</div>
        </div>
        <div class="flex gap-2">
          <button type="button" class="tap-target flex-1 rounded-xl bg-neutral-800 active:bg-neutral-700 font-semibold" @click="addPickerOpen = true">
            Add manually
          </button>
          <!-- Round-29: roll call — announces + asks each registered player is ready, by voice
               (TTS + yes/no) or tap. Disabled with nobody registered yet. -->
          <button
            type="button"
            class="tap-target flex-1 rounded-xl bg-neutral-800 active:bg-neutral-700 font-semibold disabled:opacity-40"
            :disabled="detail.participants.length === 0"
            @click="onRollCall"
          >
            Roll call
          </button>
        </div>
        <div class="flex-1 min-h-0 overflow-y-auto flex flex-col gap-1">
          <div v-if="detail.participants.length === 0" class="text-neutral-500">No one registered yet</div>
          <div
            v-for="p in detail.participants"
            :key="p.player.id"
            class="w-full flex items-center gap-3 rounded-xl bg-neutral-900 border border-neutral-800 px-3 py-2"
          >
            <Avatar :player="p.player" :size="40" />
            <span class="flex-1 font-semibold truncate">
              {{ p.player.displayName }}
              <span v-if="p.player.isGuest" class="ml-1 text-xs font-normal text-neutral-500">guest</span>
              <!-- Round-14: registration provenance (scan = badge/QR at the kiosk). -->
              <span class="ml-1 text-[10px] font-normal uppercase tracking-wide" :class="p.registeredVia === 'scan' ? 'text-emerald-500/70' : 'text-neutral-600'">
                {{ p.registeredVia === 'scan' ? 'scanned' : 'manual' }}
              </span>
            </span>
            <button
              type="button"
              class="tap-target px-3 rounded-lg bg-neutral-800 active:bg-neutral-700 text-sm text-red-400 disabled:opacity-40"
              :disabled="busyPlayerId === p.player.id"
              @click="onRemove(p.player.id)"
            >
              Remove
            </button>
          </div>
        </div>
        <button
          type="button"
          class="tap-target rounded-xl bg-emerald-600 active:bg-emerald-700 font-semibold disabled:opacity-40"
          :disabled="detail.participants.length < 2 || generating"
          @click="onGenerate"
        >
          {{ generating ? 'Generating…' : `Start tournament (${detail.participants.length})` }}
        </button>
        <PlayerSearchList v-if="addPickerOpen" @pick="onPickManual($event); addPickerOpen = false" @close="addPickerOpen = false" />
      </template>

      <!-- Phase explorer -->
      <div v-else class="flex-1 min-h-0 overflow-y-auto flex flex-col gap-4">
        <!-- All-v-all phase: standings + rounds -->
        <template v-if="detail.groupRounds.length > 0">
          <div>
            <div class="text-xs font-bold uppercase tracking-wide text-neutral-500 mb-2">All-v-all phase — standings</div>
            <div class="rounded-xl bg-neutral-900 border border-neutral-800 overflow-hidden">
              <div
                v-for="(row, i) in detail.standings"
                :key="row.player.id"
                class="flex items-center gap-3 px-3 py-1.5 border-b border-neutral-800/60 last:border-b-0"
                :class="detail.knockoutSize != null && i < detail.knockoutSize ? 'bg-emerald-950/30' : ''"
              >
                <span class="w-6 text-right font-mono text-xs text-neutral-500">{{ i + 1 }}</span>
                <Avatar :player="row.player" :size="28" />
                <span class="flex-1 truncate text-sm font-semibold">{{ row.player.displayName }}</span>
                <span class="font-mono text-xs text-neutral-400 tabular-nums">{{ row.wins }}–{{ row.losses }}</span>
                <span class="font-mono text-[10px] text-neutral-600 tabular-nums w-10 text-right">{{ row.played }} pl.</span>
              </div>
            </div>
            <p v-if="detail.format === 'rr_knockout' && detail.knockoutSize != null && !knockoutDone" class="text-[11px] text-neutral-600 mt-1">
              Top {{ detail.knockoutSize }} advance to the knockout.
            </p>
          </div>

          <div v-if="!knockoutDone">
            <div class="text-xs font-bold uppercase tracking-wide text-neutral-500 mb-2">Rounds</div>
            <div class="overflow-x-auto">
              <div class="flex gap-4 items-start" style="width: max-content">
                <div v-for="(round, ri) in detail.groupRounds" :key="ri" class="w-56 shrink-0 flex flex-col gap-2">
                  <div class="text-[11px] font-bold uppercase tracking-wide text-neutral-600 text-center">Round {{ ri + 1 }}</div>
                  <div
                    v-for="slot in round"
                    :key="slot.id"
                    class="rounded-xl border border-neutral-800 bg-neutral-900 px-3 py-2 flex flex-col gap-1.5"
                  >
                    <div
                      v-for="side in ['a', 'b'] as const"
                      :key="side"
                      class="flex items-center gap-2"
                      :class="slot.winnerPlayerId != null && (side === 'a' ? slot.playerA : slot.playerB)?.id !== slot.winnerPlayerId ? 'opacity-40' : ''"
                    >
                      <Avatar :player="side === 'a' ? slot.playerA : slot.playerB" :size="24" />
                      <span class="flex-1 truncate text-sm font-semibold">{{ (side === 'a' ? slot.playerA : slot.playerB)?.displayName ?? 'TBD' }}</span>
                      <span v-if="slot.winnerPlayerId === (side === 'a' ? slot.playerA : slot.playerB)?.id" class="text-emerald-400 text-xs font-bold">W</span>
                    </div>
                    <button
                      v-if="slot.matchId == null && slot.winnerPlayerId == null && slot.playerA && slot.playerB"
                      type="button"
                      class="mt-1 h-8 rounded-lg bg-emerald-600 active:bg-emerald-700 text-xs font-semibold"
                      @click="startSlot(slot)"
                    >
                      Start
                    </button>
                    <button
                      v-else-if="slot.matchId != null && slot.winnerPlayerId == null"
                      type="button"
                      class="mt-1 h-8 rounded-lg bg-amber-700 active:bg-amber-800 text-xs font-semibold"
                      @click="openSlotMatch(slot)"
                    >
                      Live
                    </button>
                    <button
                      v-else-if="slot.matchId != null"
                      type="button"
                      class="mt-1 h-8 rounded-lg bg-neutral-800 active:bg-neutral-700 text-xs font-semibold text-neutral-400"
                      @click="openSlotMatch(slot)"
                    >
                      Result
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>

        <!-- Knockout bracket -->
        <div v-if="detail.rounds.length > 0">
          <div class="text-xs font-bold uppercase tracking-wide text-neutral-500 mb-2">
            {{ detail.groupRounds.length > 0 ? 'Knockout' : 'Bracket' }}
          </div>
          <div v-if="bracketGeom" class="overflow-x-auto">
            <!-- Classic bracket (round-11): absolutely positioned cards on a computed grid with
                 an SVG underlay drawing per-feeder elbow connectors; a connector goes emerald
                 once its feeder slot has a winner, so the champion's path lights up. -->
            <div class="relative" :style="{ width: `${bracketGeom.width}px`, height: `${bracketGeom.height}px` }">
              <svg
                class="absolute inset-0 pointer-events-none"
                :width="bracketGeom.width"
                :height="bracketGeom.height"
                :viewBox="`0 0 ${bracketGeom.width} ${bracketGeom.height}`"
              >
                <path
                  v-for="(c, ci) in bracketGeom.connectors"
                  :key="ci"
                  :d="c.d"
                  fill="none"
                  stroke-width="2.5"
                  stroke-linejoin="round"
                  :class="c.won ? 'stroke-emerald-500' : 'stroke-neutral-600'"
                />
              </svg>
              <template v-for="(round, ri) in detail.rounds" :key="ri">
                <div
                  class="absolute text-[11px] font-bold uppercase tracking-wide text-neutral-600 text-center"
                  :style="{ left: `${bracketGeom.colX(ri)}px`, top: '0px', width: '240px' }"
                >
                  {{ knockoutRoundLabel(ri, detail.rounds.length) }}
                </div>
                <div
                  v-for="(slot, si) in round"
                  :key="slot.id"
                  class="absolute w-60 -translate-y-1/2 rounded-xl border px-3 py-2 flex flex-col gap-1.5"
                  :class="slot.isBye ? 'border-neutral-800/60 bg-neutral-900/40' : 'border-neutral-800 bg-neutral-900'"
                  :style="{ left: `${bracketGeom.colX(ri)}px`, top: `${bracketGeom.centerY(ri, si)}px` }"
                >
                  <div
                    v-for="side in ['a', 'b'] as const"
                    :key="side"
                    class="flex items-center gap-2"
                    :class="slot.winnerPlayerId != null && (side === 'a' ? slot.playerA : slot.playerB)?.id !== slot.winnerPlayerId ? 'opacity-40' : ''"
                  >
                    <Avatar :player="side === 'a' ? slot.playerA : slot.playerB" :size="26" />
                    <span class="flex-1 truncate text-sm font-semibold">
                      {{ (side === 'a' ? slot.playerA : slot.playerB)?.displayName ?? (slot.isBye ? '' : 'TBD') }}
                    </span>
                    <span v-if="slot.winnerPlayerId === (side === 'a' ? slot.playerA : slot.playerB)?.id" class="text-emerald-400 text-xs font-bold">W</span>
                  </div>
                  <div v-if="slot.isBye" class="text-[10px] uppercase tracking-wide text-neutral-600">Bye</div>
                  <button
                    v-else-if="slot.matchId == null && slot.winnerPlayerId == null && slot.playerA && slot.playerB"
                    type="button"
                    class="mt-1 h-8 rounded-lg bg-emerald-600 active:bg-emerald-700 text-xs font-semibold"
                    @click="startSlot(slot)"
                  >
                    Start
                  </button>
                  <button
                    v-else-if="slot.matchId != null && slot.winnerPlayerId == null"
                    type="button"
                    class="mt-1 h-8 rounded-lg bg-amber-700 active:bg-amber-800 text-xs font-semibold"
                    @click="openSlotMatch(slot)"
                  >
                    Live
                  </button>
                  <button
                    v-else-if="slot.matchId != null"
                    type="button"
                    class="mt-1 h-8 rounded-lg bg-neutral-800 active:bg-neutral-700 text-xs font-semibold text-neutral-400"
                    @click="openSlotMatch(slot)"
                  >
                    Result
                  </button>
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>
    </template>
    <RollCallPanel />
  </div>
</template>
